import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ImageOff, Loader2, RotateCcw, Check } from 'lucide-react';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'auto_replaced', label: 'Auto-Replaced' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'restored', label: 'Restored' },
];

const StatusBadge = ({ status }) => {
  const map = {
    auto_replaced: 'bg-amber-100 text-amber-800 border-amber-300',
    confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    restored: 'bg-slate-100 text-slate-700 border-slate-300',
  };
  return (
    <Badge className={`${map[status] || 'bg-gray-100 text-gray-700'} border`} data-testid={`flag-status-${status}`}>
      {status.replace('_', ' ')}
    </Badge>
  );
};

const FlaggedProfileImages = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get(`/admin/flagged-images?status=${tab}`),
        api.get('/admin/flagged-images/summary'),
      ]);
      setItems(list.data?.items || []);
      setSummary(sum.data || null);
    } catch (e) {
      toast.error('Failed to load flagged images');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/flagged-images/${id}/restore`, { note: 'Admin restore (false positive)' });
      toast.success('Original photo restored');
      load();
    } catch (e) {
      toast.error('Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const confirm = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/flagged-images/${id}/confirm`, { note: 'Admin confirmed removal' });
      toast.success('Removal confirmed');
      load();
    } catch (e) {
      toast.error('Confirm failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="flagged-images-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} data-testid="flagged-images-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Settings
          </Button>
          <div className="flex items-center gap-2">
            <ImageOff className="w-5 h-5 text-amber-600" />
            <h1 className="text-lg font-semibold text-gray-900">Flagged Profile Images</h1>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="flagged-images-summary">
            <Card><CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Total flagged</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Auto-replaced</p>
              <p className="text-2xl font-bold text-amber-700">{summary.by_status?.auto_replaced || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Confirmed</p>
              <p className="text-2xl font-bold text-emerald-700">{summary.by_status?.confirmed || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Restored</p>
              <p className="text-2xl font-bold text-slate-700">{summary.by_status?.restored || 0}</p>
            </CardContent></Card>
          </div>
        )}

        <div className="flex gap-2 flex-wrap" data-testid="flagged-images-tabs">
          {STATUS_TABS.map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={tab === t.key ? 'default' : 'outline'}
              onClick={() => setTab(t.key)}
              data-testid={`flagged-images-tab-${t.key}`}
            >
              {t.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-gray-500" data-testid="flagged-images-empty">
            No flagged images in this view.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <Card key={it.id} data-testid={`flagged-image-row-${it.id}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
                  <div className="flex gap-3">
                    <div className="text-center">
                      <img src={it.original_url} alt="flagged" className="w-16 h-16 rounded object-cover border border-red-200" onError={(e) => { e.target.style.opacity = 0.3; }} />
                      <p className="text-[10px] text-red-600 mt-1">flagged</p>
                    </div>
                    <div className="text-center">
                      <img src={it.replacement_url} alt="replacement" className="w-16 h-16 rounded object-cover border border-emerald-200" />
                      <p className="text-[10px] text-emerald-600 mt-1">replacement</p>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{it.talent_name || 'Unknown talent'}</p>
                      <StatusBadge status={it.review_status} />
                    </div>
                    <p className="text-xs text-gray-500 truncate">{it.email}</p>
                    <p className="text-xs text-gray-600 mt-1">{it.reason}</p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Source: {it.source} · Hash {it.matched_hash?.slice(0, 12)}… · {it.detected_at ? new Date(it.detected_at).toLocaleString() : ''}
                    </p>
                  </div>
                  {it.review_status === 'auto_replaced' && (
                    <div className="flex sm:flex-col gap-2 shrink-0">
                      <Button size="sm" variant="outline" disabled={busyId === it.id} onClick={() => restore(it.id)} data-testid={`flagged-image-restore-${it.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                      <Button size="sm" disabled={busyId === it.id} onClick={() => confirm(it.id)} data-testid={`flagged-image-confirm-${it.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Confirm
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlaggedProfileImages;
